import {
  AssignmentIndOutlined,
  OpenInNew,
  PersonAddAltOutlined,
} from '@mui/icons-material';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Pagination,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import { Navigate } from 'react-router-dom';

import Label from '../components/label';
import PageTitle from '../components/PageTitle';
import { GeneralLayout } from '../layouts/GeneralLayout';
import {
  useGetDutyEmployeesQuery,
  useListStatementsQuery,
  useSetDutyEmployeeMutation,
} from '../store/statementRegistry/statementRegistryApi';
import { useGetSemestersQuery } from '../store/system/systemApi';
import { useGetUserDetailsQuery } from '../store/user/userApi';

const SESSION = 0;
const PAGE_SIZE = 50;

const statusLabel = (status) => {
  if (status === 0) return <Label color="success">Открыта</Label>;
  if (status === 1) return <Label color="default">Закрыта</Label>;
  return <Label color="warning">На утверждении</Label>;
};

const dateString = (value) => value ? new Date(value).toLocaleDateString('ru-RU') : '—';
const optionValue = (value) => value === '' ? null : Number(value);

export default function StatementRegistryPage() {
  const { data: semestersData } = useGetSemestersQuery();
  const { data: userData, isLoading: userDetailsLoading } = useGetUserDetailsQuery();
  const registryAvailable = userData?.statementRegistryAvailable === true;

  const [semesterIds, setSemesterIds] = useState(() => {
    const selectedSemesterId = Number(localStorage.getItem('selectedSemesterId'));
    return selectedSemesterId ? [selectedSemesterId] : [];
  });
  const [type, setType] = useState('all');
  const [status, setStatus] = useState('');
  const [facultyId, setFacultyId] = useState('');
  const [educationFormId, setEducationFormId] = useState('');
  const [chairId, setChairId] = useState('');
  const [groupId, setGroupId] = useState('');
  const [statementNumber, setStatementNumber] = useState('');
  const [search, setSearch] = useState('');
  const [debouncedStatementNumber, setDebouncedStatementNumber] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [page, setPage] = useState(1);
  const [selected, setSelected] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [employeeId, setEmployeeId] = useState('');
  const [message, setMessage] = useState(null);
  const [removalTarget, setRemovalTarget] = useState(null);

  useEffect(() => {
    if (!semesterIds.length && semestersData?.data?.length) {
      setSemesterIds([Number(semestersData.data[0].key)]);
    }
  }, [semesterIds.length, semestersData]);

  useEffect(() => {
    const timeout = window.setTimeout(() => {
      setDebouncedStatementNumber(statementNumber);
      setDebouncedSearch(search);
    }, 400);
    return () => window.clearTimeout(timeout);
  }, [statementNumber, search]);

  const request = useMemo(() => ({
    semesterIds,
    facultyId: optionValue(facultyId),
    educationFormId: optionValue(educationFormId),
    chairId: optionValue(chairId),
    groupId: optionValue(groupId),
    includeSession: type !== 'practice',
    includePractice: type !== 'session',
    status: optionValue(status),
    statementNumber: debouncedStatementNumber.trim() || null,
    search: debouncedSearch.trim() || null,
    page,
    pageSize: PAGE_SIZE,
  }), [semesterIds, facultyId, educationFormId, chairId, groupId, type, status, debouncedStatementNumber, debouncedSearch, page]);

  const { data: response, isFetching, error, refetch } = useListStatementsQuery(request, {
    skip: !registryAvailable || !semesterIds.length,
  });
  const registry = response?.data;
  const canManage = registry?.canManageDutyEmployees === true;
  const items = registry?.items ?? [];
  const selectedSemesters = semestersData?.data?.filter((semester) =>
    semesterIds.includes(Number(semester.key))) ?? [];
  const selectedGroup = registry?.groups?.find((group) => group.key === String(groupId)) ?? null;
  const selectedRows = items.filter((item) => selected.includes(`${item.type}-${item.statementId}`));
  const { data: employeesResponse, isFetching: employeesLoading } = useGetDutyEmployeesQuery(undefined, {
    skip: !registryAvailable || !dialogOpen,
  });
  const selectedEmployee = employeesResponse?.data?.find((employee) =>
    employee.key === String(employeeId)) ?? null;
  const [setDutyEmployee, { isLoading: dutySaving }] = useSetDutyEmployeeMutation();

  useEffect(() => {
    setPage(1);
    setSelected([]);
  }, [semesterIds, type, status, facultyId, educationFormId, chairId, groupId, debouncedStatementNumber, debouncedSearch]);

  const toggleRow = (row) => {
    const key = `${row.type}-${row.statementId}`;
    setMessage(null);
    setSelected((current) => {
      if (current.includes(key)) return current.filter((value) => value !== key);
      const currentRows = items.filter((item) => current.includes(`${item.type}-${item.statementId}`));
      if (currentRows.some((item) => item.type !== row.type)) {
        setMessage({ severity: 'warning', text: 'Сессионные и практические ведомости назначаются отдельно.' });
        return current;
      }
      return [...current, key];
    });
  };

  const openDutyDialog = () => {
    if (!selectedRows.length) return;
    if (selectedRows[0].type === SESSION) {
      const selectedChairIds = new Set(selectedRows.flatMap((row) =>
        (row.chairs ?? []).map((chair) => chair.key)));
      if (selectedChairIds.size > 1) {
        setMessage({
          severity: 'error',
          text: 'Выбранные ведомости относятся к разным кафедрам, выберите ведомости одной кафедры.',
        });
        return;
      }
    }
    setEmployeeId('');
    setDialogOpen(true);
  };

  const saveDuty = async (isDuty, targetEmployeeId = employeeId, rows = selectedRows) => {
    if (!targetEmployeeId || !rows.length) return;
    setMessage(null);
    try {
      const result = await setDutyEmployee({
        type: rows[0].type,
        statementIds: rows.map((item) => item.statementId),
        employeeId: Number(targetEmployeeId),
        isDuty,
      }).unwrap();
      if (result?.success === false) throw new Error(result.errors?.join('\n'));
      setDialogOpen(false);
      setRemovalTarget(null);
      setSelected([]);
      setMessage({ severity: 'success', text: isDuty ? 'Дежурный преподаватель назначен.' : 'Отметка дежурного преподавателя снята.' });
      refetch();
    } catch (requestError) {
      const errors = requestError?.data?.errors;
      setMessage({ severity: 'error', text: errors?.join('\n') || requestError?.message || 'Не удалось сохранить изменения.' });
    }
  };

  const openStatement = (row) => {
    const path = row.type === SESSION
      ? `/statementresults/${row.statementId}`
      : `/practice-statement-results/${row.statementId}`;
    window.open(path, '_blank', 'noopener,noreferrer');
  };

  const resetFilters = () => {
    setType('all');
    setStatus('');
    setFacultyId('');
    setEducationFormId('');
    setChairId('');
    setGroupId('');
    setStatementNumber('');
    setSearch('');
  };

  const renderOptions = (options) => options?.map((option) => (
    <MenuItem key={option.key} value={option.key}>{option.value}</MenuItem>
  ));

  if (userDetailsLoading)
    return <Stack alignItems="center" justifyContent="center" sx={{ minHeight: '60vh' }}><CircularProgress /></Stack>;

  if (!registryAvailable)
    return <Navigate to="/" replace />;

  return (
    <GeneralLayout>
      <PageTitle title="Список ведомостей" />
      <Container>
        <Stack direction="column" padding="120px 0px 40px 100px" spacing={3}>
          <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" spacing={2}>
            <Box>
              <Typography variant="h4">Список ведомостей</Typography>
              <Typography color="text.secondary" sx={{ mt: 0.5 }}>
                Просмотр сессионных и практических ведомостей
              </Typography>
            </Box>
            {canManage && (
              <Button
                variant="contained"
                startIcon={<PersonAddAltOutlined />}
                disabled={!selectedRows.length}
                onClick={openDutyDialog}
              >
                Назначить дежурного
              </Button>
            )}
          </Stack>

          {message && <Alert severity={message.severity} onClose={() => setMessage(null)}>{message.text}</Alert>}
          {error && <Alert severity="error">Не удалось загрузить список ведомостей.</Alert>}

          <Card>
            <CardContent>
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', lg: 'repeat(4, 1fr)' }, gap: 2 }}>
                <Autocomplete
                  multiple
                  disableCloseOnSelect
                  options={semestersData?.data ?? []}
                  value={selectedSemesters}
                  getOptionLabel={(option) => option?.value ?? ''}
                  isOptionEqualToValue={(option, value) => option.key === value.key}
                  onChange={(_, values) => setSemesterIds(values.map((semester) => Number(semester.key)))}
                  noOptionsText="Семестры не найдены"
                  renderInput={(params) => <TextField {...params} size="small" label="Семестры" />}
                />
                <FormControl size="small" fullWidth>
                  <InputLabel>Вид ведомости</InputLabel>
                  <Select value={type} label="Вид ведомости" onChange={(event) => setType(event.target.value)}>
                    <MenuItem value="all">Все</MenuItem>
                    <MenuItem value="session">Сессионные</MenuItem>
                    <MenuItem value="practice">Практические</MenuItem>
                  </Select>
                </FormControl>
                <FormControl size="small" fullWidth>
                  <InputLabel>Статус</InputLabel>
                  <Select value={status} label="Статус" onChange={(event) => setStatus(event.target.value)}>
                    <MenuItem value="">Все</MenuItem>
                    <MenuItem value="0">Открытые</MenuItem>
                    <MenuItem value="1">Закрытые</MenuItem>
                    <MenuItem value="2">На утверждении</MenuItem>
                  </Select>
                </FormControl>
                <FormControl size="small" fullWidth>
                  <InputLabel>Факультет</InputLabel>
                  <Select
                    value={facultyId}
                    label="Факультет"
                    onChange={(event) => {
                      setFacultyId(event.target.value);
                      setGroupId('');
                    }}
                  >
                    <MenuItem value="">Все доступные</MenuItem>
                    {renderOptions(registry?.faculties)}
                  </Select>
                </FormControl>
                <FormControl size="small" fullWidth>
                  <InputLabel>Форма обучения</InputLabel>
                  <Select value={educationFormId} label="Форма обучения" onChange={(event) => setEducationFormId(event.target.value)}>
                    <MenuItem value="">Все</MenuItem>
                    {renderOptions(registry?.educationForms)}
                  </Select>
                </FormControl>
                <FormControl size="small" fullWidth>
                  <InputLabel>Кафедра</InputLabel>
                  <Select value={chairId} label="Кафедра" onChange={(event) => setChairId(event.target.value)}>
                    <MenuItem value="">Все</MenuItem>
                    {renderOptions(registry?.chairs)}
                  </Select>
                </FormControl>
                <Autocomplete
                  options={registry?.groups ?? []}
                  value={selectedGroup}
                  getOptionLabel={(option) => option?.value ?? ''}
                  isOptionEqualToValue={(option, value) => option.key === value.key}
                  onChange={(_, value) => setGroupId(value?.key ?? '')}
                  noOptionsText="Группы не найдены"
                  renderInput={(params) => <TextField {...params} size="small" label="Группа" />}
                />
                <TextField size="small" label="Номер ведомости" value={statementNumber} onChange={(event) => setStatementNumber(event.target.value)} />
                <TextField
                  size="small"
                  label="Дисциплина, практика или группа"
                  value={search}
                  onChange={(event) => setSearch(event.target.value)}
                  sx={{ gridColumn: { lg: 'span 2' } }}
                />
                <Button variant="text" onClick={resetFilters}>Сбросить фильтры</Button>
              </Box>
            </CardContent>
          </Card>

          <Card sx={{ position: 'relative', minHeight: 220 }}>
            {isFetching && (
              <Stack
                alignItems="center"
                justifyContent="center"
                spacing={1.5}
                sx={{
                  position: 'absolute',
                  inset: 0,
                  zIndex: 2,
                  bgcolor: 'rgba(255, 255, 255, 0.82)',
                  backdropFilter: 'blur(2px)',
                }}
              >
                <CircularProgress size={44} />
                <Typography variant="subtitle1" fontWeight={600}>Загружаем ведомости...</Typography>
              </Stack>
            )}
            <TableContainer sx={{ opacity: isFetching ? 0.45 : 1, overflowX: 'hidden' }}>
              <Table
                size="small"
                sx={{
                  width: '100%',
                  tableLayout: 'fixed',
                  '& .MuiTableCell-root': { px: 1 },
                }}
              >
                <TableHead>
                  <TableRow>
                    {canManage && <TableCell padding="checkbox" sx={{ width: 56, pr: 2 }} />}
                    <TableCell sx={{ width: '12%' }}>Ведомость</TableCell>
                    <TableCell sx={{ width: '17%' }}>Дисциплина / практика</TableCell>
                    <TableCell sx={{ width: '8%' }}>Группа</TableCell>
                    <TableCell sx={{ width: '15%' }}>Подразделение</TableCell>
                    <TableCell sx={{ width: 120 }}>Дата / статус</TableCell>
                    <TableCell sx={{ width: '17%' }}>Дежурный преподаватель</TableCell>
                    <TableCell align="center" sx={{ width: 60 }}>Действия</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {!isFetching && !items.length && (
                    <TableRow><TableCell colSpan={canManage ? 8 : 7} align="center" sx={{ py: 6 }}>Ведомости по выбранным условиям не найдены</TableCell></TableRow>
                  )}
                  {items.map((row) => {
                    const key = `${row.type}-${row.statementId}`;
                    return (
                      <TableRow
                        key={key}
                        hover
                        selected={selected.includes(key)}
                        onClick={(event) => {
                          if (!row.canManageDutyEmployees || event.target.closest('button, a, input, [role="button"]')) return;
                          toggleRow(row);
                        }}
                        sx={{ cursor: row.canManageDutyEmployees ? 'pointer' : 'default' }}
                      >
                        {canManage && (
                          <TableCell padding="checkbox" sx={{ pr: 2 }}>
                            <Checkbox
                              checked={selected.includes(key)}
                              disabled={!row.canManageDutyEmployees}
                              onChange={() => toggleRow(row)}
                              inputProps={{ 'aria-label': `Выбрать ведомость ${row.statementNumber}` }}
                            />
                          </TableCell>
                        )}
                        <TableCell>
                          <Typography variant="subtitle2">№ {row.statementNumber || 'без номера'}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            {row.type === SESSION ? 'Сессионная' : 'Практическая'} · {row.passingTypeName}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ maxWidth: 280 }}>{row.name}</Typography>
                          <Typography variant="caption" color="text.secondary">{row.controlTypeName}</Typography>
                        </TableCell>
                        <TableCell sx={{ overflowWrap: 'anywhere' }}>{row.groupName}</TableCell>
                        <TableCell>
                          <Typography variant="body2">{row.facultyName}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            {row.chairs?.map((item) => item.value).join(', ') || 'Кафедра не указана'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Stack spacing={0.75} alignItems="flex-start">
                            <Typography variant="body2">{dateString(row.eventDate)}</Typography>
                            {statusLabel(row.status)}
                          </Stack>
                        </TableCell>
                        <TableCell>
                          <Stack spacing={0.5} sx={{ maxWidth: 310 }}>
                            <Stack direction="row" flexWrap="wrap" gap={0.75}>
                              {row.dutyEmployees?.length ? row.dutyEmployees.map((employee) => (
                              <Tooltip
                                key={employee.key}
                                title={row.dutyEmployeesLastChangedBy
                                  ? `Последнее изменение: ${row.dutyEmployeesLastChangedBy}, ${dateString(row.dutyEmployeesLastChangedOn)}`
                                  : ''}
                              >
                                <Chip
                                  size="small"
                                  label={employee.value}
                                  sx={{ maxWidth: '100%' }}
                                  onDelete={row.canManageDutyEmployees
                                    ? () => setRemovalTarget({ row, employee })
                                    : undefined}
                                />
                              </Tooltip>
                              )) : <Typography variant="body2" color="text.secondary">Не назначен</Typography>}
                            </Stack>
                            {row.dutyEmployeesLastChangedBy && (
                              <Typography variant="caption" color="text.secondary">
                                Изменил: {row.dutyEmployeesLastChangedBy}, {dateString(row.dutyEmployeesLastChangedOn)}
                              </Typography>
                            )}
                          </Stack>
                        </TableCell>
                        <TableCell align="center" sx={{ whiteSpace: 'nowrap', px: 0.5 }}>
                          <Tooltip title="Просмотр информации">
                            <IconButton size="small" onClick={() => openStatement(row)}><OpenInNew fontSize="small" /></IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
            {registry?.totalCount > PAGE_SIZE && (
              <Stack alignItems="center" sx={{ py: 2 }}>
                <Pagination count={Math.ceil(registry.totalCount / PAGE_SIZE)} page={page} onChange={(_, value) => setPage(value)} />
              </Stack>
            )}
          </Card>
        </Stack>
      </Container>

      <Dialog open={dialogOpen} onClose={() => !dutySaving && setDialogOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>
          <Stack direction="row" spacing={1} alignItems="center">
            <AssignmentIndOutlined color="primary" />
            <span>Назначение дежурного преподавателя</span>
          </Stack>
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <Alert severity="info">
              Выбрано ведомостей: {selectedRows.length}.
              {!registry?.canManageMultipleDutyEmployees && ' Новый преподаватель заменит ранее отмеченного дежурного.'}
            </Alert>
            <Autocomplete
              options={employeesResponse?.data ?? []}
              value={selectedEmployee}
              disabled={employeesLoading}
              getOptionLabel={(option) => option?.value ?? ''}
              isOptionEqualToValue={(option, value) => option.key === value.key}
              onChange={(_, value) => setEmployeeId(value?.key ?? '')}
              noOptionsText="Преподаватели не найдены"
              renderInput={(params) => <TextField {...params} size="small" label="Преподаватель" />}
            />
            {employeesLoading && <Stack direction="row" spacing={1} alignItems="center"><CircularProgress size={18} /><Typography variant="body2">Загружаем сотрудников...</Typography></Stack>}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)} disabled={dutySaving}>Отмена</Button>
          <Button variant="contained" onClick={() => saveDuty(true)} disabled={!employeeId || dutySaving}>
            {dutySaving ? <CircularProgress size={20} /> : 'Отправить на утверждение'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(removalTarget)}
        onClose={() => !dutySaving && setRemovalTarget(null)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Удаление дежурного преподавателя</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mt: 1 }}>
            Снять отметку дежурного преподавателя у сотрудника «{removalTarget?.employee?.value}»?
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRemovalTarget(null)} disabled={dutySaving}>Отмена</Button>
          <Button
            variant="contained"
            color="warning"
            disabled={dutySaving}
            onClick={() => saveDuty(false, removalTarget.employee.key, [removalTarget.row])}
          >
            {dutySaving ? <CircularProgress size={20} /> : 'Отправить на утверждение'}
          </Button>
        </DialogActions>
      </Dialog>
    </GeneralLayout>
  );
}
